import { useRef, useState } from 'react';
import { CheckCircle2, Eye, EyeOff, Shield, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CERTIFICADO_APELIDO_MAX,
  uploadCertificado,
  type CertificadoCreateResponse,
} from '@/api/client';
import { cn } from '@/lib/utils';
import { notifyMutationError } from '@/lib/errors';
import { useNotification } from '@/context/NotificationProvider';
import { useAuth } from '@/hooks/useAuth';

/**
 * Metadados extraídos do certificado após upload.
 * Usado pelo OnboardingPage para preencher nome/CPF automaticamente.
 */
export interface CertMetadata {
  nome: string;
  cpf: string;
  emissor: string;
  validade: string;
  oab?: { numero: string; uf: string };
}

interface CertificateUploadCardProps {
  onSuccess: (metadata: CertMetadata) => void;
  /** Onboarding: dropzone mais baixa e sem o apelido. */
  compact?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

/** Marcador do passo (1, 2) — vira ✓ quando o passo está feito. */
function StepMark({ done, children }: { done?: boolean; children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
        done
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
          : 'border border-primary/25 bg-primary/10 text-primary',
      )}
    >
      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : children}
    </span>
  );
}

/**
 * Cadastro do certificado digital (.p12/.pfx) na ordem natural: primeiro o
 * ARQUIVO, depois a SENHA (que pertence ao arquivo). A senha nasce desabilitada
 * e só habilita com o anexo — a interface impede a ordem invertida em vez de
 * explicá-la.
 *
 * Sem moldura própria: quem chama decide o cartão em volta (o onboarding usa o
 * card da etapa; a tela Certificados, o painel de "Adicionar").
 *
 * Apelido opcional (rótulo pessoal, editável depois na lista) — fora do
 * onboarding (`compact`): zero configuração no primeiro acesso.
 */
export function CertificateUploadCard({
  onSuccess,
  compact = false,
}: CertificateUploadCardProps) {
  const { refreshUser } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [senhaCertificado, setSenhaCertificado] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [apelido, setApelido] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const senhaInputRef = useRef<HTMLInputElement>(null);

  const acceptFile = (candidate: File | null) => {
    if (!candidate) return;
    const ext = candidate.name.toLowerCase().split('.').pop();
    if (ext !== 'p12' && ext !== 'pfx') {
      showError('Formato inválido. Selecione um arquivo .p12 ou .pfx');
      return;
    }
    setFile(candidate);
    // O anexo conclui o passo 1 — o foco segue para a senha (habilitada no
    // próximo render, por isso o adiamento).
    setTimeout(() => senhaInputRef.current?.focus(), 0);
  };

  const trocarArquivo = () => {
    setFile(null);
    setSenhaCertificado('');
    // O chip (com o botão focado) some — o foco volta ao seletor de arquivo.
    setTimeout(() => document.getElementById('arquivo')?.focus(), 0);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      showError('Selecione um arquivo .p12 ou .pfx');
      return;
    }

    if (!senhaCertificado) {
      showError('Informe a senha do certificado.');
      return;
    }

    try {
      setUploading(true);

      const form = new FormData();
      form.append('senha_certificado', senhaCertificado);
      form.append('arquivo', file);
      if (apelido.trim()) form.append('apelido', apelido.trim());

      const data: CertificadoCreateResponse = await uploadCertificado(form);
      showSuccess('Certificado cadastrado com sucesso!');

      const metadata: CertMetadata = {
        nome: data.nome ?? '',
        cpf: data.cpf_certificado ?? '',
        emissor: data.emissor ?? '',
        validade: data.validade_fim ?? '',
      };

      setSenhaCertificado('');
      setFile(null);
      setApelido('');

      await refreshUser();
      onSuccess(metadata);
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleUpload} className="space-y-4">
      {/* Passo 1 — arquivo */}
      <div className="space-y-2">
        <div className="flex items-center gap-2.5">
          <StepMark done={!!file}>1</StepMark>
          <span className="text-sm font-semibold">Anexe o certificado</span>
          <span className="ml-auto text-xs text-muted-foreground">A1 · .pfx ou .p12</span>
        </div>

        {/* O <input> fica DENTRO do <label> (como no DropzonePdf): clicar em
            qualquer ponto abre o seletor, e o input, só visualmente oculto,
            recebe o foco do teclado (Enter/Espaço abrem o seletor). */}
        <label
          htmlFor="arquivo"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            acceptFile(e.dataTransfer.files?.[0] ?? null);
          }}
          className={cn(
            'flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors focus-within:ring-2 focus-within:ring-ring',
            compact ? 'min-h-[92px]' : 'min-h-[116px]',
            dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/60 hover:bg-muted/50',
            file && 'hidden',
          )}
        >
          <Upload className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">Arraste o arquivo ou clique para escolher</span>
          <input
            id="arquivo"
            type="file"
            accept=".p12,.pfx"
            className="sr-only"
            onChange={(e) => {
              acceptFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
        </label>

        {file && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-600/30 bg-emerald-50 px-3.5 py-3 dark:bg-emerald-950/40">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            <div className="min-w-0 flex-1 leading-tight">
              <p className="text-sm font-semibold [overflow-wrap:anywhere]">{file.name}</p>
              <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
            </div>
            <Button
              type="button"
              variant="link"
              className="min-h-11 shrink-0 px-1 text-xs sm:h-auto sm:min-h-0 sm:p-0"
              onClick={trocarArquivo}
              disabled={uploading}
            >
              Trocar
            </Button>
          </div>
        )}
      </div>

      {/* Passo 2 — senha (só faz sentido depois do arquivo) */}
      <div className="space-y-2">
        <div className="flex items-center gap-2.5">
          <StepMark>2</StepMark>
          <Label htmlFor="senha-certificado" className="font-semibold">
            Senha do certificado
          </Label>
        </div>
        <div className="relative">
          <Input
            ref={senhaInputRef}
            id="senha-certificado"
            type={showSenha ? 'text' : 'password'}
            placeholder={file ? 'Senha usada ao emitir o certificado' : 'Anexe o certificado primeiro'}
            value={senhaCertificado}
            onChange={(e) => setSenhaCertificado(e.target.value)}
            disabled={!file}
            className="pr-11"
            required
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowSenha((v) => !v)}
            className="absolute right-0 top-1/2 h-10 w-10 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            disabled={!file}
            aria-label={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {!compact && file && (
        <div className="space-y-1.5">
          <Label htmlFor="apelido-certificado">Apelido (opcional)</Label>
          <Input
            id="apelido-certificado"
            placeholder="Ex.: A1 do escritório"
            value={apelido}
            maxLength={CERTIFICADO_APELIDO_MAX}
            onChange={(e) => setApelido(e.target.value)}
          />
        </div>
      )}

      <Button type="submit" disabled={uploading || !file} className="w-full gap-2">
        {uploading && <Spinner size={16} className="text-inherit" />}
        {uploading ? 'Enviando...' : 'Cadastrar certificado'}
      </Button>

      {/* Reasseguro em uma linha — o detalhe técnico mora no tooltip. */}
      <p className="flex flex-wrap items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <Shield className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Criptografado. Assina só quando você peticionar.
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" variant="link" className="min-h-11 px-1 text-xs sm:h-auto sm:min-h-0 sm:p-0">
              Saiba mais
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs leading-relaxed">
              Seu certificado é cifrado com envelope encryption (chave mestra no
              AWS KMS) e a senha é protegida com AES-256-GCM, nunca armazenada em
              texto. Ele é decifrado no servidor apenas no momento de assinar,
              quando você peticiona — e você pode desativá-lo quando quiser.
            </p>
          </TooltipContent>
        </Tooltip>
      </p>
    </form>
  );
}
